import React, { Component, Fragment } from 'react';
import { get, size, set, has, split, values, orderBy } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Table, IconButton, Icon } from 'rsuite';
import DateCell from '../widgets/custom-cell/Date';
import Pagination from '../Pagination';
import PaginationType from '../../interfaces/PaginationType';
import computeTableHeight from '../../helpers/computeTableHeight';
import DeleteDownloadRequestModal from '../DeleteConfirmationModal';

import {
  LabelByStatus,
  Context as DownloadRequestsContext
} from '../../constants/download-request';
import HeaderCell from '../widgets/custom-cell/HeaderCell';

const { Column, Cell } = Table;

const UserCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    {get(`${dataKey}.firstname`, rowData)} {get(`${dataKey}.lastname`, rowData)}
  </Cell>
);

UserCell.propTypes = {
  rowData: PropTypes.shape({}),
  dataKey: PropTypes.string.isRequired
};

UserCell.defaultProps = {
  rowData: null
};

const StatusCell = ({ rowData, dataKey, ...props }) => (
  <Cell {...props} className="link-group">
    {LabelByStatus[get(dataKey, rowData)]}
  </Cell>
);

StatusCell.propTypes = {
  rowData: PropTypes.shape({}),
  dataKey: PropTypes.string.isRequired
};

StatusCell.defaultProps = {
  rowData: null
};

const ActionCell = ({ rowData, onView, onDelete, allowDelete, ...props }) => (
  <Cell {...props} className="link-group">
    <IconButton
      icon={<Icon icon="eye" />}
      appearance="link"
      onClick={() => {
        onView(rowData);
      }}
      style={{ top: -8 }}
    />

    <IconButton
      icon={<Icon icon="trash" style={{ width: 32 }} />}
      appearance="link"
      color="red"
      onClick={() => onDelete(rowData)}
      disabled={!allowDelete}
      style={{ top: -8, paddingLeft: 18 }}
    />
  </Cell>
);

ActionCell.propTypes = {
  rowData: PropTypes.shape({}),
  onView: PropTypes.func,
  onDelete: PropTypes.func,
  allowDelete: PropTypes.bool
};

ActionCell.defaultProps = {
  rowData: null,
  onView: () => {},
  onDelete: () => {},
  allowDelete: false
};

class DownloadRequestList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showConfirmDelete: false,
      reportToDelete: null
    };
  }

  componentDidMount() {
    const { onInit, isFetched, context, query } = this.props;
    if (!isFetched || !has('context', query)) {
      const order = get('order', query);
      onInit({ context, order });
    }
  }

  componentDidUpdate({ isDeleted: prevIsDeleted, isDeleting: prevIsDeleting }) {
    const { isDeleted, isDeleting } = this.props;
    const deleteSucceed = !prevIsDeleted && isDeleted;
    const deleteFailed = prevIsDeleting && !isDeleting && !isDeleted;
    if (deleteSucceed || deleteFailed) {
      this.handleHideDeleteModal();
    }
  }

  getData() {
    const { downloadRequests, query } = this.props;
    const [sortColumn, sortType] = split(':', get('order', query));
    return orderBy(sortColumn, sortType, downloadRequests);
  }

  handleChangePage = page => {
    const { onChangePage, loadedPages, query } = this.props;
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    onChangePage(nextPageQuery, isPageLoaded);
  };

  handleChangeLimit = limit => {
    const { onChangeLimit, query } = this.props;
    const newQuery = { ...query, limit, page: 1 };
    onChangeLimit(newQuery);
  };

  handleOpenDeleteModal = reportToDelete => {
    this.setState({ showConfirmDelete: true, reportToDelete });
  };

  handleHideDeleteModal = () => {
    this.setState({ showConfirmDelete: false });
  };

  handleConfirmDelete = () => {
    const { reportToDelete } = this.state;
    const { onDelete } = this.props;
    onDelete(reportToDelete);
  };

  renderPagination() {
    const { pagination } = this.props;
    return (
      <Pagination
        page={pagination.currentPage}
        limit={pagination.limit}
        total={pagination.totalCount}
        onChangePage={this.handleChangePage}
        onChangeLimit={this.handleChangeLimit}
      />
    );
  }

  render() {
    const {
      downloadRequests,
      isFetching,
      onView,
      isDeleting,
      allowDelete
    } = this.props;
    const { showConfirmDelete, reportToDelete } = this.state;
    return (
      <Fragment>
        <Table
          height={computeTableHeight(size(downloadRequests))}
          data={this.getData()}
          loading={isFetching}
          bordered
        >
          <Column width={50} align="center" fixed>
            <HeaderCell>Id</HeaderCell>
            <Cell dataKey="id" />
          </Column>
          <Column width={150} align="center" fixed>
            <HeaderCell>Report Name</HeaderCell>
            <Cell dataKey="name" />
          </Column>
          <Column width={150} fixed>
            <HeaderCell>User</HeaderCell>
            <UserCell dataKey="user" />
          </Column>
          <Column width={150}>
            <HeaderCell>Date Created</HeaderCell>
            <DateCell dataKey="createdAt" />
          </Column>
          <Column width={150}>
            <HeaderCell>Date Completed</HeaderCell>
            <DateCell dataKey="completedAt" />
          </Column>
          <Column width={120} fixed>
            <HeaderCell>Status</HeaderCell>
            <StatusCell dataKey="status" />
          </Column>
          {onView && (
            <Column width={120} fixed>
              <HeaderCell>Action</HeaderCell>
              <ActionCell
                dataKey="id"
                onView={onView}
                onDelete={this.handleOpenDeleteModal}
                allowDelete={allowDelete}
              />
            </Column>
          )}
        </Table>
        {this.renderPagination()}

        {reportToDelete && (
          <DeleteDownloadRequestModal
            message={`Are you sure you want to delete these results ${
              reportToDelete.name
            }?  If Yes click the "Yes" button.`}
            show={showConfirmDelete}
            onHide={this.handleHideDeleteModal}
            onConfirm={this.handleConfirmDelete}
            report={reportToDelete}
            isDeleting={isDeleting}
          />
        )}
      </Fragment>
    );
  }
}

DownloadRequestList.propTypes = {
  downloadRequests: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  context: PropTypes.oneOf(values(DownloadRequestsContext)).isRequired,
  query: PropTypes.shape({}).isRequired,
  loadedPages: PropTypes.shape({}).isRequired,
  pagination: PaginationType.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isFetched: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  isDeleted: PropTypes.bool.isRequired,
  onInit: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  onChangeLimit: PropTypes.func.isRequired,
  onView: PropTypes.func,
  allowDelete: PropTypes.bool
};

DownloadRequestList.defaultProps = {
  onView: null,
  allowDelete: false
};

export default DownloadRequestList;
